<template>
  <div class="create-site">
    <!-- Header -->
    <div class="create-site__header">
      <div class="create-site__header-content">
        <div>
          <h1 class="create-site__title">New Site</h1>
          <p class="create-site__subtitle">
            {{ currentStepDescription }}
          </p>
        </div>
        <div class="create-site__header-actions">
          <themed-button
            variant="outline"
            @click="handleCancel"
            :disabled="loading"
          >
            Cancel
          </themed-button>
          <themed-button
            v-if="step === 'confirmation'"
            @click="createSite"
            :disabled="!readyForCreation || loading"
          >
            <span v-if="!loading">Create New Site</span>
            <span v-else>Creating...</span>
          </themed-button>
        </div>
      </div>
    </div>

    <!-- Progress Steps - Figma Design with Connecting Lines -->
    <div class="create-site__progress">
      <template v-for="(stepInfo, index) in steps">
        <div :key="'step-' + stepInfo.key" class="create-site__progress-item">
          <div 
            class="create-site__progress-circle"
            :class="{
              'create-site__progress-circle--active': step === stepInfo.key,
              'create-site__progress-circle--complete': isStepComplete(stepInfo.key)
            }"
          >
            <theme-icon 
              v-if="isStepComplete(stepInfo.key)" 
              icon="check" 
              type="icons" 
              class="create-site__progress-icon"
            />
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span 
            class="create-site__progress-label"
            :class="{
              'create-site__progress-label--active': step === stepInfo.key
            }"
          >
            {{ stepInfo.label }}
          </span>
        </div>
        <!-- Connecting Line -->
        <div v-if="index < steps.length - 1" :key="'line-' + stepInfo.key" class="create-site__progress-line"></div>
      </template>
    </div>

    <!-- Step 1: Subsidiary Selection -->
    <div v-if="step === 'subsidiary'" class="create-site__content">
      <div class="create-site__card">
        <themed-table
          :data="subsidiaries"
          :columns="subsidiaryColumns"
          :hoverable="true"
          @row-click="handleSubsidiaryRowClick"
        >
          <template #column-select="{ row }">
            <input
              type="radio"
              :checked="selectedSubsidiary && selectedSubsidiary.id === row.id"
              @change="selectSubsidiary(row)"
              @click.stop
              class="create-site__radio"
            />
          </template>
        </themed-table>

        <div class="create-site__card-footer">
          <themed-button
            variant="outline"
            @click="showCreateSubsidiary"
          >
            <theme-icon icon="add" type="icons" class="create-site__button-icon" />
            <span>Create New Subsidiary</span>
          </themed-button>
          <themed-button
            @click="step = 'building'"
            :disabled="!selectedSubsidiary"
          >
            Use this Subsidiary
          </themed-button>
        </div>
      </div>
    </div>

    <!-- Step 2: Building Search -->
    <div v-if="step === 'building'" class="create-site__content">
      <!-- Selected Subsidiary Card -->
      <div class="create-site__card create-site__card--compact">
        <div class="create-site__selected-info">
          <div>
            <h3 class="create-site__selected-label">Selected Subsidiary</h3>
            <p class="create-site__selected-name">{{ selectedSubsidiary.name }}</p>
            <p class="create-site__selected-address">
              {{ selectedSubsidiary.address }}, {{ selectedSubsidiary.city }}, 
              {{ selectedSubsidiary.state }} {{ selectedSubsidiary.postal_code }}
            </p>
          </div>
          <themed-button
            variant="outline"
            size="sm"
            @click="step = 'subsidiary'"
          >
            Change
          </themed-button>
        </div>
      </div>

      <!-- Building Search Card -->
      <div class="create-site__card">
        <h3 class="create-site__card-title">Search for Building</h3>
        
        <div class="create-site__search-wrapper">
          <themed-search
            v-model="searchQuery"
            placeholder="Search by building name or location..."
            @input="debounceSearch"
          />
        </div>

        <!-- Search Results -->
        <div v-if="searchQuery && buildings.all.length > 0" class="create-site__results">
          <p class="create-site__results-count">
            {{ buildings.all.length }} building{{ buildings.all.length !== 1 ? 's' : '' }} found
          </p>
          <div class="create-site__building-grid">
            <div
              v-for="building in buildings.all"
              :key="building.id"
              @click="selectBuilding(building.id)"
              class="create-site__building-card"
              :class="{ 'create-site__building-card--selected': buildingId === building.id }"
              role="button"
              tabindex="0"
              @keypress.enter="selectBuilding(building.id)"
              @keypress.space.prevent="selectBuilding(building.id)"
            >
              <div class="create-site__building-image">
                <img 
                  v-if="building.image && building.image.view_url"
                  :src="building.image.view_url" 
                  :alt="building.address_line_1"
                  class="create-site__building-img"
                />
                <div v-else class="create-site__building-placeholder">
                  <theme-icon icon="building" type="icons" />
                </div>
                <div 
                  v-if="buildingId === building.id"
                  class="create-site__building-selected-badge"
                >
                  <theme-icon icon="check" type="icons" />
                </div>
              </div>
              <div class="create-site__building-details">
                <p class="create-site__building-name">{{ building.address_line_1 }}</p>
                <p class="create-site__building-location">
                  {{ building.city }}, {{ building.state }} {{ building.postal_code }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- No Results -->
        <div v-if="searchQuery && !loading && buildings.all.length === 0" class="themed-empty-state">
          <div class="themed-empty-state__icon">
            <theme-icon icon="close" type="icons" />
          </div>
          <h3 class="themed-empty-state__title">No buildings found</h3>
          <p class="themed-empty-state__message">
            We couldn't find any buildings matching "{{ searchQuery }}"
          </p>
          <themed-button
            @click="showCreateBuilding"
            class="themed-empty-state__action"
          >
            <theme-icon icon="add" type="icons" class="create-site__button-icon" />
            <span>Create New Building</span>
          </themed-button>
        </div>

        <!-- Empty State -->
        <div v-if="!searchQuery" class="themed-empty-state">
          <div class="themed-empty-state__icon">
            <theme-icon icon="search" type="icons" />
          </div>
          <p class="themed-empty-state__message">
            Start typing to search for a building
          </p>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="create-site__loading">
          <theme-icon icon="spinner-icon" type="icons" class="create-site__loading-icon" />
        </div>

        <div class="create-site__card-footer">
          <themed-button
            variant="outline"
            @click="step = 'subsidiary'"
          >
            <theme-icon icon="chevron-left" type="icons" class="create-site__button-icon" />
            <span>Back</span>
          </themed-button>
          <themed-button
            @click="step = 'lease'"
            :disabled="!buildingId"
          >
            Continue
          </themed-button>
        </div>
      </div>
    </div>

    <!-- Step 3: Lease or Own -->
    <div v-if="step === 'lease'" class="create-site__content">
      <div class="create-site__lease-options">
        <div
          @click="selectLeaseOption('Leased')"
          class="create-site__lease-card"
          :class="{ 'create-site__lease-card--selected': leaseOrOwn === 'Leased' }"
          role="button"
          tabindex="0"
          @keypress.enter="selectLeaseOption('Leased')"
          @keypress.space.prevent="selectLeaseOption('Leased')"
          aria-label="Select Leased option"
        >
          <div 
            v-if="leaseOrOwn === 'Leased'" 
            class="create-site__lease-check"
          >
            <theme-icon icon="check-large" type="icons" />
          </div>
          <div class="create-site__lease-icon">
            <theme-icon icon="leased" type="icons" />
          </div>
          <span class="create-site__lease-label create-site__lease-label--leased">Leased</span>
        </div>

        <div
          @click="selectLeaseOption('Owned')"
          class="create-site__lease-card"
          :class="{ 'create-site__lease-card--selected': leaseOrOwn === 'Owned' }"
          role="button"
          tabindex="0"
          @keypress.enter="selectLeaseOption('Owned')"
          @keypress.space.prevent="selectLeaseOption('Owned')"
          aria-label="Select Owned option"
        >
          <div 
            v-if="leaseOrOwn === 'Owned'" 
            class="create-site__lease-check"
          >
            <theme-icon icon="check-large" type="icons" />
          </div>
          <div class="create-site__lease-icon">
            <theme-icon icon="owned" type="icons" />
          </div>
          <span class="create-site__lease-label create-site__lease-label--owned">Owned</span>
        </div>
      </div>

      <div class="create-site__card-footer">
        <themed-button
          variant="outline"
          @click="step = 'building'"
        >
          <theme-icon icon="chevron-left" type="icons" class="create-site__button-icon" />
          <span>Back</span>
        </themed-button>
        <themed-button
          @click="step = 'confirmation'"
          :disabled="!leaseOrOwn"
        >
          Continue
        </themed-button>
      </div>
    </div>

    <!-- Step 4: Confirmation -->
    <div v-if="step === 'confirmation'" class="create-site__content">
      <div v-if="readyForCreation" class="create-site__confirmation">
        <p class="create-site__confirmation-text">
          Almost there, confirm the information below then click '<strong>Create Site</strong>'.
        </p>
        
        <div class="create-site__confirmation-card">
          <div class="create-site__confirmation-image">
            <img 
              :src="building.image.view_url" 
              :alt="building.address_line_1"
              class="create-site__confirmation-img"
            />
          </div>
          <div class="create-site__confirmation-details">
            <div class="create-site__confirmation-address">
              <p class="create-site__confirmation-title">{{ building.address_line_1 }}</p>
              <p class="create-site__confirmation-subtitle">
                {{ building.city }}, {{ building.state }} {{ building.postal_code }}
              </p>
            </div>
            <div class="create-site__confirmation-info">
              <p class="create-site__confirmation-item">
                <theme-icon icon="check" type="icons" class="create-site__confirmation-icon" />
                <span>{{ leaseOrOwn }}</span>
              </p>
              <p class="create-site__confirmation-item">
                <theme-icon icon="check" type="icons" class="create-site__confirmation-icon" />
                <span>{{ selectedSubsidiary.name }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="create-site__incomplete">
        <div class="create-site__alert create-site__alert--error">
          Site data incomplete, complete the previous steps to create your new site.
        </div>
      </div>
    </div>

    <!-- Modals -->
    <create-building-wizard
      @buildingCreated="setBuilding($event)"
      @close="closeBuildingWizard"
      for-site
    />

    <create-subsidiary
      :client-id="clientId"
      @subsidiaryCreated="addSubsidiary($event)"
    />

    <notifications classes="notification" position="bottom right" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ThemedButton from '../ThemedButton.vue';
import ThemedSearch from '../ThemedSearch.vue';
import ThemedTable from '../ThemedTable.vue';
import CreateBuildingWizard from '../buildings/CreateBuildingWizard.vue';
import CreateSubsidiary from '../subsidiaries/CreateSubsidiary.vue';

export default {
  name: 'CreateSite',
  
  components: {
    ThemedButton,
    ThemedSearch,
    ThemedTable,
    CreateBuildingWizard,
    CreateSubsidiary
  },
  
  props: {
    subsidiaryData: {
      type: Array,
      required: true
    },
    
    showSection: {
      type: String,
      default: 'subsidiary'
    },
    
    clientId: {
      type: Number,
      required: false,
      default: null
    },
    
    wizardMode: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      step: this.showSection,
      subsidiaries: this.subsidiaryData,
      selectedSubsidiary: null,
      leaseOrOwn: '',
      buildingId: '',
      searchQuery: '',
      loading: false,
      errors: []
    };
  },
  
  computed: {
    ...mapState(['buildings']),
    
    steps() {
      return [
        { key: 'subsidiary', label: 'Subsidiary Information' },
        { key: 'building', label: 'Building Information' },
        { key: 'lease', label: 'Lease Information' },
        { key: 'confirmation', label: 'Create Site' }
      ];
    },
    
    currentStepDescription() {
      const descriptions = {
        subsidiary: 'What Subsidiary is this Site for?',
        building: 'Search for the building or create a new one',
        lease: 'Is this Site leased or owned?',
        confirmation: 'Review and confirm your site information'
      };
      return descriptions[this.step] || '';
    },
    
    subsidiaryColumns() {
      return [
        {
          field: 'select',
          label: 'Select',
          type: 'custom',
          width: '60px'
        },
        {
          field: 'name',
          label: 'Name',
          sortable: true
        },
        {
          field: 'address',
          label: 'Address',
          sortable: true
        },
        {
          field: 'city',
          label: 'City',
          sortable: true
        },
        {
          field: 'state',
          label: 'State',
          sortable: true
        },
        {
          field: 'postal_code',
          label: 'Zip',
          sortable: true
        }
      ];
    },
    
    readyForCreation() {
      return this.selectedSubsidiary && this.selectedSubsidiary.id && this.leaseOrOwn && this.buildingId;
    },
    
    building() {
      return _.find(this.buildings.all, ['id', this.buildingId]);
    },
  },
  
  methods: {
    isStepComplete(stepKey) {
      const completionMap = {
        subsidiary: !!this.selectedSubsidiary,
        building: !!this.buildingId,
        lease: !!this.leaseOrOwn,
        confirmation: this.readyForCreation
      };
      return completionMap[stepKey] || false;
    },
    
    handleSubsidiaryRowClick({ row }) {
      this.selectSubsidiary(row);
    },
    
    selectSubsidiary(subsidiary) {
      this.selectedSubsidiary = subsidiary;
    },
    
    selectBuilding(id) {
      this.buildingId = id;
    },
    
    selectLeaseOption(option) {
      this.leaseOrOwn = option;
    },
    
    debounceSearch: _.debounce(function() {
      this.searchBuildings();
    }, 400),
    
    searchBuildings() {
      if (!this.searchQuery) {
        this.clearBuildings();
        return;
      }
      
      this.loading = true;
      
      this.$store.dispatch('loadBuildings', this.searchQuery)
        .takeAtLeast(300)
        .then(() => {
          this.loading = false;
        });
    },
    
    setBuilding(building) {
      this.buildingId = building.id;
      this.buildings.all.push(building);
      this.$modal.hide('create-building-wizard');
    },
    
    clearBuildings() {
      this.buildings.all = [];
      this.buildings.filtered = [];
    },
    
    showCreateSubsidiary() {
      this.$modal.show('create-subsidiary');
    },
    
    addSubsidiary(subsidiary) {
      this.subsidiaries.push(subsidiary);
      this.selectedSubsidiary = subsidiary;
      this.$modal.hide('create-subsidiary');
    },
    
    showCreateBuilding() {
      this.$modal.show('create-building-wizard');
    },
    
    closeBuildingWizard() {
      this.$modal.hide('create-building-wizard');
    },
    
    createSite() {
      if (!this.readyForCreation) {
        return;
      }
      
      this.loading = true;
      
      axios.post('/sites', {
        client_id: this.selectedSubsidiary.client_id,
        subsidiary_id: this.selectedSubsidiary.id,
        building_id: this.buildingId,
        lease_or_own: this.leaseOrOwn
      }).then((response) => {
        if (this.wizardMode) {
          this.$emit('next', response.data.site);
        } else {
          window.location = '/sites/' + response.data.site.id;
        }
      }).catch((error) => {
        console.error(error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to create site'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    
    handleCancel() {
      if (this.wizardMode) {
        this.$emit('cancel');
      } else {
        window.location.href = '/sites';
      }
    }
  }
};
</script>
